import React, { useState } from 'react';
import { Card, Form, Button, Spinner } from 'react-bootstrap';
import { Play } from 'lucide-react';

import SiteIdInput from './SiteIdInput';
import StatusNotification from './StatusNotification';
import { validateSiteIds } from '../utility/validators';
import { addDemoSites, deleteDemoSites } from '../services/api';

const DemoSitesIndexing = () => {
    // State for managing demo site IDs input
    const [demoSiteInput, setDemoSiteInput] = useState('');
    // State for tracking if operations are in progress
    const [isAdding, setIsAdding] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState(null);

    /**
     * Formats API response into user-friendly notification message for demo sites
     * @param {Object} data - API response with success and failed arrays
     * @param {string} action - The action performed ('added' or 'deleted')
     * @returns {Object} - Formatted notification object
     */
    const formatDemoResponseMessage = (data, action) => {
        if (!data || !data.success || !data.failed) {
            return {
                type: 'success',
                message: `Demo sites ${action} successfully.`,
            };
        }

        const successSites = data.success.join(', ');
        const failedSites = data.failed.join(', ');

        let message = '';

        if (data.success.length > 0) {
            message = `Successfully ${action} ${data.success.length} demo site(s) ${action === 'added' ? 'to' : 'from'} DynamoDB: ${successSites}.`;
        }

        if (data.failed.length > 0) {
            const actionVerb = action === 'added' ? 'add' : 'delete';
            const isPlural = data.failed.length > 1;
            const accountText = isPlural
                ? 'accounts already exist'
                : 'account already exists';
            const presentText = isPlural
                ? 'accounts are not present'
                : 'account is not present';
            const specificReason =
                action === 'added'
                    ? `Demo site ${accountText} in DynamoDB`
                    : `Demo site ${presentText} in DynamoDB`;
            message += `${data.success.length > 0 ? '\n\n' : ''}Failed to ${actionVerb} demo site(s): ${failedSites}. ${specificReason}.`;
        }

        return {
            type: data.success.length > 0 ? 'success' : 'danger',
            message: message,
            useHtml: true,
        };
    };

    /**
     * Handles errors during demo sites operations
     * @param {Object} error - Error object from API call
     * @param {string} action - The action that failed ('add' or 'delete')
     * @returns {Object} - Formatted error notification
     */
    const handleDemoSitesError = (error, action) => {
        console.error(`Demo Sites ${action} API Error:`, error);

        // Use specific error message from API if available, otherwise fallback to generic
        const errorMessage = `Could not ${action} demo sites.`;

        return {
            type: 'danger',
            message: errorMessage,
        };
    };

    /**
     * Validates input and returns validation result
     * @returns {Object|null} - Returns validation object if valid, null if invalid
     */
    const validateInput = () => {
        const validation = validateSiteIds(demoSiteInput, 'demo');

        if (!validation.isValid) {
            setNotification({ type: 'danger', message: validation.error });
            return null;
        }

        return validation;
    };

    // Function to handle the Add Demo Sites button click
    const handleAddDemoSites = async () => {
        const validation = validateInput();
        if (!validation) return;

        // Set loading state
        setIsAdding(true);

        // Show progress notification immediately
        setNotification({
            type: 'info',
            message: 'Adding demo site(s)...',
        });

        try {
            console.log('Adding demo sites:', validation.siteIds);

            // Call our API service
            const data = await addDemoSites(validation.siteIds);

            // Format response message
            const formattedResponse = formatDemoResponseMessage(data, 'added');
            setNotification(formattedResponse);

            // Clear the input on successful operation
            if (data.success && data.success.length > 0) {
                setDemoSiteInput('');
            }
        } catch (error) {
            setNotification(handleDemoSitesError(error, 'add'));
        } finally {
            // Reset loading state
            setIsAdding(false);
        }
    };

    // Function to handle the Delete Demo Sites button click
    const handleDeleteDemoSites = async () => {
        const validation = validateInput();
        if (!validation) return;

        // Set loading state
        setIsDeleting(true);

        // Show progress notification immediately
        setNotification({
            type: 'info',
            message: 'Deleting demo site(s)...',
        });

        try {
            console.log('Deleting demo sites:', validation.siteIds);

            // Call our API service
            const data = await deleteDemoSites(validation.siteIds);

            // Format response message
            const formattedResponse = formatDemoResponseMessage(
                data,
                'deleted'
            );
            setNotification(formattedResponse);

            // Clear the input on successful operation
            if (data.success && data.success.length > 0) {
                setDemoSiteInput('');
            }
        } catch (error) {
            setNotification(handleDemoSitesError(error, 'delete'));
        } finally {
            // Reset loading state
            setIsDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Demo Sites Input Section */}
            <Card className="bg-card shadow-sm">
                <Card.Header className="text-left">
                    <Card.Title className="font-medium text-foreground text-left">
                        Enter Demo Site ID(s)
                    </Card.Title>
                </Card.Header>
                <Card.Body className="space-y-4">
                    <Form>
                        <SiteIdInput
                            value={demoSiteInput}
                            onChange={setDemoSiteInput}
                            placeholder="E.g., demosite01, demosite02"
                            helpText="Separate multiple demo site IDs with commas or new lines. Maximum 10 demo site IDs per request."
                        />

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <Button
                                variant="primary"
                                onClick={handleAddDemoSites}
                                disabled={
                                    isAdding ||
                                    !demoSiteInput.trim() ||
                                    isDeleting
                                }
                                className="flex items-center gap-2"
                            >
                                {isAdding ? (
                                    <>
                                        <Spinner
                                            className="me-2"
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <Play size={16} className="me-2" />
                                        <b>Add Demo Site(s)</b>
                                    </>
                                )}
                            </Button>

                            <Button
                                variant="danger"
                                onClick={handleDeleteDemoSites}
                                disabled={
                                    isDeleting ||
                                    !demoSiteInput.trim() ||
                                    isAdding
                                }
                                className="flex items-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <Spinner
                                            className="me-2"
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Play size={16} className="me-2" />
                                        <b>Delete Demo Site(s)</b>
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Use our StatusNotification component */}
                        <StatusNotification notification={notification} />
                    </Form>
                </Card.Body>
            </Card>

            {/* Footer - Demo Sites Specific */}
            <div className="text-center py-4">
                <p className="text-sm text-muted-foreground italic">
                    Note: This section should be used to inject a manual
                    indexing of a 'Demo Site' that is not on a live domain. This
                    site's content will be indexed for 180 days.
                </p>
            </div>
        </div>
    );
};

export default DemoSitesIndexing;
